import React, { useEffect, useMemo, useState } from 'react';
import { auditFetch } from '../utils/auditFetch';
import {
  alarmSignOptions,
  buildPreTriageSummary,
  classifyPreTriage,
  emptyPreTriageForm,
  getTriagePriorityLabel,
  normalizeTriagePriority,
} from '../clinical/preTriage';

const PreTriagePanel = ({
  appointment,
  user,
  editable = false,
  compact = false,
  initialValue = null,
  onSaved,
}) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const appointmentId = appointment?.id || '';
  const [preTriage, setPreTriage] = useState(initialValue || appointment?.preTriage || null);
  const [form, setForm] = useState({
    ...emptyPreTriageForm,
    ...(initialValue || appointment?.preTriage || {}),
  });
  const [message, setMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setPreTriage(initialValue || appointment?.preTriage || null);
    setForm({
      ...emptyPreTriageForm,
      ...(initialValue || appointment?.preTriage || {}),
    });
  }, [appointment?.id, appointment?.preTriage, initialValue]);

  useEffect(() => {
    if (!appointmentId) return undefined;
    let cancelled = false;
    setIsLoading(true);
    auditFetch(`${backendUrl}/api/pretriage/${appointmentId}`, {}, user)
      .then((res) => (res.ok ? res.json() : { preTriage: null }))
      .then((data) => {
        if (cancelled || !data?.preTriage) return;
        setPreTriage(data.preTriage);
        setForm({ ...emptyPreTriageForm, ...data.preTriage });
      })
      .catch(() => {
        if (!cancelled && editable) setMessage('Pretriaje pendiente para esta cita.');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [appointmentId, backendUrl, editable, user]);

  const calculatedPriority = useMemo(() => classifyPreTriage(form), [form]);
  const visiblePriority = normalizeTriagePriority(preTriage?.priority || calculatedPriority);
  const selectedAlarmLabels = alarmSignOptions
    .filter(([key]) => (preTriage?.alarmSigns || form.alarmSigns || {})[key])
    .map(([, label]) => label);

  const updateAlarmSign = (key, checked) => {
    setForm((current) => ({
      ...current,
      alarmSigns: {
        ...(current.alarmSigns || {}),
        [key]: checked,
      },
    }));
  };

  const savePreTriage = async () => {
    if (!appointmentId || isSaving) return;
    if (!form.mainSymptoms.trim() || !form.evolutionTime.trim()) {
      setMessage('Registra sintomas principales y tiempo de evolucion.');
      return;
    }

    setIsSaving(true);
    setMessage('');
    try {
      const response = await auditFetch(`${backendUrl}/api/pretriage/${appointmentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      }, user);
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'No se pudo guardar el pretriaje.');
      setPreTriage(data.preTriage);
      setForm({ ...emptyPreTriageForm, ...data.preTriage });
      setMessage(`Pretriaje guardado. Prioridad ${getTriagePriorityLabel(data.preTriage.priority)}.`);
      if (onSaved) onSaved(data);
    } catch (error) {
      setMessage(error.message || 'No se pudo guardar el pretriaje.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={`pretriage-panel ${compact ? 'pretriage-panel-compact' : ''}`}>
      <div className="pretriage-header">
        <div>
          <h3>Pretriaje</h3>
          <p>Sintomas, evolucion, signos de alarma y prioridad previa.</p>
        </div>
        <span className={`triage-priority triage-priority-${visiblePriority}`}>
          {getTriagePriorityLabel(visiblePriority)}
        </span>
      </div>

      {editable ? (
        <div className="pretriage-form">
          <label>
            <span>Sintomas principales</span>
            <textarea
              value={form.mainSymptoms}
              onChange={(event) => setForm((current) => ({ ...current, mainSymptoms: event.target.value }))}
              placeholder="Ej. tos, fiebre, dolor abdominal, mareo"
            />
          </label>
          <label>
            <span>Tiempo de evolucion</span>
            <input
              value={form.evolutionTime}
              onChange={(event) => setForm((current) => ({ ...current, evolutionTime: event.target.value }))}
              placeholder="Ej. 2 dias, 1 semana, inicio hoy"
            />
          </label>
          <div className="pretriage-alarm-grid">
            {alarmSignOptions.map(([key, label]) => (
              <label key={key} className="pretriage-alarm-option">
                <input
                  type="checkbox"
                  checked={Boolean(form.alarmSigns?.[key])}
                  onChange={(event) => updateAlarmSign(key, event.target.checked)}
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
          <label>
            <span>Antecedentes relevantes</span>
            <textarea
              value={form.relevantHistory}
              onChange={(event) => setForm((current) => ({ ...current, relevantHistory: event.target.value }))}
              placeholder="Ej. embarazo, diabetes, hipertension, asma, alergias, medicamentos"
            />
          </label>
          <button type="button" className="btn btn-primary full-width" onClick={savePreTriage} disabled={isSaving}>
            {isSaving ? 'Guardando...' : 'Guardar pretriaje'}
          </button>
        </div>
      ) : (
        <div className="pretriage-summary">
          {isLoading && !preTriage ? (
            <p>Cargando pretriaje...</p>
          ) : preTriage ? (
            <>
              <p><strong>Resumen:</strong> {buildPreTriageSummary(preTriage)}</p>
              <p><strong>Sintomas:</strong> {preTriage.mainSymptoms || 'No registrado'}</p>
              <p><strong>Evolucion:</strong> {preTriage.evolutionTime || 'No registrado'}</p>
              <p><strong>Signos de alarma:</strong> {selectedAlarmLabels.length > 0 ? selectedAlarmLabels.join('; ') : 'No referidos'}</p>
              <p><strong>Antecedentes:</strong> {preTriage.relevantHistory || 'No registrados'}</p>
            </>
          ) : (
            <p>Pretriaje pendiente.</p>
          )}
        </div>
      )}

      {message && <p className="save-message">{message}</p>}
    </div>
  );
};

export default PreTriagePanel;
